// flx.js - a minimal, behaviour-faithful re-implementation of the parts of
// Flixel 2.50 (AS3) that CLOP uses, on top of Canvas 2D and WebAudio.
//
// Timing model (identical to FlxGame in the original SWF):
//   * logic step  = uint(1000 / 60) = 16 ms  -> 62.5 logic ticks per second
//   * FlxG.elapsed = 0.016 s per tick
//   * accumulator clamped to uint(2000 / 30 - 1) = 65 ms  (max 4 ticks per frame)
//   * while (accumulator > step) { step(); accumulator -= step; }
'use strict';

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function argbToCss(c) {
  const a = ((c >>> 24) & 255) / 255, r = (c >>> 16) & 255, g = (c >>> 8) & 255, b = c & 255;
  return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
}
function rgbToCss(c) {
  return 'rgb(' + ((c >>> 16) & 255) + ',' + ((c >>> 8) & 255) + ',' + (c & 255) + ')';
}
function nudge(v) { return v + (v > 0 ? 1e-7 : -1e-7); }

class FlxPoint {
  constructor(x = 0, y = 0) { this.x = x; this.y = y; }
  make(x = 0, y = 0) { this.x = x; this.y = y; return this; }
}

// ---------------------------------------------------------------------------
// FlxG
// ---------------------------------------------------------------------------
const FlxG = {
  width: 320,
  height: 240,
  scale: 1,           // render scale: 1 = classic 320x240 buffer, N = "HD" N x resolution
  rotateAtDraw: false, // HD: rotate frame 0 at draw time instead of using baked rotation frames
  texts: new Set(),    // live FlxText instances (re-rasterised when the render scale changes)
  // Switch render scale at runtime: resize camera buffers, re-render text,
  // and let the game/state swap scale-dependent assets.
  setScale(scale) {
    FlxG.scale = scale;
    FlxG.rotateAtDraw = scale > 1;
    for (const c of FlxG.cameras) c.resizeBuffer();
    for (const t of FlxG.texts) t.calcText();
    if (FlxG._game) FlxG._game.onScaleChange();
  },
  elapsed: 0,
  timeScale: 1,
  volume: 0.5,        // Flixel default
  mute: false,
  bgColor: 0xff000000,
  keys: null,
  camera: null,
  cameras: [],
  images: {},         // name -> HTMLImageElement
  soundBuffers: {},   // name -> AudioBuffer
  audio: null,        // AudioContext
  liveSounds: new Set(),
  _game: null,
  log() { /* console.log.apply(console, arguments); */ },
  random() { return Math.random(); },
  fade(color = 0xffffffff, duration = 1, onComplete = null, force = false) {
    for (const c of FlxG.cameras) c.fade(color, duration, onComplete, force);
  },
  switchState(state) { FlxG._game._requestedState = state; },
  resetInput() { FlxG.keys.reset(); },
  resetCameras() {
    FlxG.cameras.length = 0;
    FlxG.camera = new FlxCamera(0, 0, FlxG.width, FlxG.height);
    FlxG.cameras.push(FlxG.camera);
  },
  updateInput() { FlxG.keys.update(); },
  updateCameras() { for (const c of FlxG.cameras) if (c.exists && c.active) c.update(); },
  lockCameras() { for (const c of FlxG.cameras) if (c.exists && c.visible) c.fill(); },
  unlockCameras() { for (const c of FlxG.cameras) if (c.exists && c.visible) c.drawFX(); },
  pauseSounds() { for (const s of FlxG.liveSounds) if (s.exists && s.active) s.pause(); },
  playSounds() { for (const s of FlxG.liveSounds) if (s.exists) s.play(); },
  stopAllSounds() { for (const s of Array.from(FlxG.liveSounds)) s.stop(); },
  updateVolumes() { for (const s of FlxG.liveSounds) s.updateTransform(); },
};

// ---------------------------------------------------------------------------
// Keyboard (semantics of org.flixel.system.input.Input / Keyboard)
// ---------------------------------------------------------------------------
class Keyboard {
  constructor() {
    this._map = {};
    this._codes = {};
    const add = (code, name) => { this._codes[code] = name; this._map[name] = { current: 0, last: 0 }; };
    for (let i = 0; i < 26; i++) { const L = String.fromCharCode(65 + i); add('Key' + L, L); }
    for (let i = 0; i < 10; i++) { add('Digit' + i, i === 0 ? 'ZERO' : ['ONE','TWO','THREE','FOUR','FIVE','SIX','SEVEN','EIGHT','NINE'][i - 1]); }
    add('Numpad0', 'NUMPADZERO'); add('Minus', 'MINUS'); add('NumpadSubtract', 'NUMPADMINUS');
    add('Equal', 'PLUS'); add('NumpadAdd', 'NUMPADPLUS'); add('Enter', 'ENTER'); add('Space', 'SPACE');
    add('Escape', 'ESCAPE'); add('ArrowLeft', 'LEFT'); add('ArrowRight', 'RIGHT'); add('ArrowUp', 'UP'); add('ArrowDown', 'DOWN');
  }
  handleKeyDown(code) {
    const o = this._map[this._codes[code]]; if (!o) return;
    o.current = o.current > 0 ? 1 : 2;
  }
  handleKeyUp(code) {
    const o = this._map[this._codes[code]]; if (!o) return;
    o.current = o.current > 0 ? -1 : 0;
  }
  update() {
    for (const k in this._map) {
      const o = this._map[k];
      if (o.last === -1 && o.current === -1) o.current = 0;
      else if (o.last === 2 && o.current === 2) o.current = 1;
      o.last = o.current;
    }
  }
  reset() { for (const k in this._map) { this._map[k].current = 0; this._map[k].last = 0; } }
  justPressed(name) { return this._map[name].current === 2; }
  justReleased(name) { return this._map[name].current === -1; }
  pressed(name) { return this._map[name].current > 0; }
}

// ---------------------------------------------------------------------------
// FlxCamera
// ---------------------------------------------------------------------------
class FlxCamera {
  constructor(x, y, width, height) {
    this.x = x; this.y = y; this.width = width; this.height = height;
    this.exists = true; this.active = true; this.visible = true;
    this.target = null; this.deadzone = null; this.bounds = null;
    this.scroll = new FlxPoint();
    this._point = new FlxPoint();
    this.bgColor = FlxG.bgColor;
    this.buffer = document.createElement('canvas');
    this.ctx = this.buffer.getContext('2d', { alpha: false, willReadFrequently: true });
    this.resizeBuffer();
    this._fxFadeColor = 0; this._fxFadeDuration = 0; this._fxFadeComplete = null; this._fxFadeAlpha = 0;
    this._fxFlashColor = 0; this._fxFlashDuration = 0; this._fxFlashComplete = null; this._fxFlashAlpha = 0;
  }
  resizeBuffer() {
    this.buffer.width = this.width * FlxG.scale; this.buffer.height = this.height * FlxG.scale;
    this.ctx.imageSmoothingEnabled = false;
  }
  update() {
    if (this.target != null) {
      if (this.deadzone == null) {
        this.focusOn(this.target.getMidpoint(this._point));
      }
    }
    if (this.bounds != null) {
      if (this.scroll.x < this.bounds.left) this.scroll.x = this.bounds.left;
      if (this.scroll.x > this.bounds.right - this.width) this.scroll.x = this.bounds.right - this.width;
      if (this.scroll.y < this.bounds.top) this.scroll.y = this.bounds.top;
      if (this.scroll.y > this.bounds.bottom - this.height) this.scroll.y = this.bounds.bottom - this.height;
    }
    if (this._fxFlashAlpha > 0) {
      this._fxFlashAlpha -= FlxG.elapsed / this._fxFlashDuration;
      if (this._fxFlashAlpha <= 0 && this._fxFlashComplete != null) this._fxFlashComplete();
    }
    if (this._fxFadeAlpha > 0 && this._fxFadeAlpha < 1) {
      this._fxFadeAlpha += FlxG.elapsed / this._fxFadeDuration;
      if (this._fxFadeAlpha >= 1) {
        this._fxFadeAlpha = 1;
        if (this._fxFadeComplete != null) this._fxFadeComplete();
      }
    }
  }
  follow(target) { this.target = target; this.deadzone = null; }
  focusOn(pt) {
    const px = nudge(pt.x), py = nudge(pt.y);
    this.scroll.make(px - this.width * 0.5, py - this.height * 0.5);
  }
  setBounds(x = 0, y = 0, w = 0, h = 0) {
    this.bounds = { left: x, top: y, right: x + w, bottom: y + h };
    this.update();
  }
  fade(color = 0xffffffff, duration = 1, onComplete = null, force = false) {
    if (!force && this._fxFadeAlpha > 0) return;
    this._fxFadeColor = color;
    if (duration <= 0) duration = Number.MIN_VALUE;
    this._fxFadeDuration = duration;
    this._fxFadeComplete = onComplete;
    this._fxFadeAlpha = Number.MIN_VALUE;
  }
  fill(color = 0) {
    if (color === 0) color = this.bgColor;
    const ctx = this.ctx;
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = argbToCss(color);
    ctx.fillRect(0, 0, this.buffer.width, this.buffer.height);
  }
  drawFX() {
    if (this._fxFlashAlpha > 0) {
      let a = this._fxFlashColor >>> 24;
      this.fill(((Math.floor((a <= 0 ? 255 : a) * this._fxFlashAlpha) & 255) * 16777216) + (this._fxFlashColor & 0xFFFFFF));
    }
    if (this._fxFadeAlpha > 0) {
      let a = this._fxFadeColor >>> 24;
      this.fill(((Math.floor((a <= 0 ? 255 : a) * this._fxFadeAlpha) & 255) * 16777216) + (this._fxFadeColor & 0xFFFFFF));
    }
  }
  // Equivalent of buffer.getPixel32(x, y) on the most recently drawn frame.
  // Reads the one pixel asked for: reading back the whole buffer cost ~20 MB
  // per frame at HD 8x, on every frame with hoof dust.
  getPixel32(x, y) {
    const S = FlxG.scale, bw = this.buffer.width, bh = this.buffer.height;
    x = Math.trunc(x * S); y = Math.trunc(y * S);
    if (x < 0 || y < 0 || x >= bw || y >= bh) return 0;
    const d = this.ctx.getImageData(x, y, 1, 1).data;
    return ((d[3] << 24) | (d[0] << 16) | (d[1] << 8) | d[2]) >>> 0;
  }
}

// ---------------------------------------------------------------------------
// FlxBasic / FlxObject / FlxSprite
// ---------------------------------------------------------------------------
const LEFT = 1, RIGHT = 16, UP = 256, DOWN = 4096, NONE = 0, FLOOR = DOWN;

class FlxBasic {
  constructor() { this.ID = -1; this.exists = true; this.active = true; this.visible = true; this.alive = true; }
  preUpdate() {}
  update() {}
  postUpdate() {}
  draw() {}
  kill() { this.alive = false; this.exists = false; }
  revive() { this.alive = true; this.exists = true; }
  destroy() {}
}

class FlxObject extends FlxBasic {
  constructor(x = 0, y = 0, width = 0, height = 0) {
    super();
    this.x = x; this.y = y; this.width = width; this.height = height;
    this.last = new FlxPoint(x, y);
    this.moves = true;
    this.touching = NONE; this.wasTouching = NONE;
    this.velocity = new FlxPoint(); this.acceleration = new FlxPoint(); this.drag = new FlxPoint();
    this.maxVelocity = new FlxPoint(10000, 10000);
    this.angle = 0; this.angularVelocity = 0; this.angularAcceleration = 0; this.angularDrag = 0; this.maxAngular = 10000;
    this.elasticity = 0;
    this.scrollFactor = new FlxPoint(1, 1);
    this._point = new FlxPoint();
  }
  preUpdate() { this.last.x = this.x; this.last.y = this.y; }
  postUpdate() {
    if (this.moves) this.updateMotion();
    this.wasTouching = this.touching;
    this.touching = NONE;
  }
  static computeVelocity(v, acc = 0, drag = 0, max = 10000) {
    if (acc !== 0) v += acc * FlxG.elapsed;
    else if (drag !== 0) {
      const d = drag * FlxG.elapsed;
      if (v - d > 0) v -= d; else if (v + d < 0) v += d; else v = 0;
    }
    if (v !== 0 && max !== 10000) { if (v > max) v = max; else if (v < -max) v = -max; }
    return v;
  }
  updateMotion() {
    let delta, vd;
    vd = (FlxObject.computeVelocity(this.angularVelocity, this.angularAcceleration, this.angularDrag, this.maxAngular) - this.angularVelocity) / 2;
    this.angularVelocity += vd; this.angle += this.angularVelocity * FlxG.elapsed; this.angularVelocity += vd;
    vd = (FlxObject.computeVelocity(this.velocity.x, this.acceleration.x, this.drag.x, this.maxVelocity.x) - this.velocity.x) / 2;
    this.velocity.x += vd; delta = this.velocity.x * FlxG.elapsed; this.velocity.x += vd; this.x += delta;
    vd = (FlxObject.computeVelocity(this.velocity.y, this.acceleration.y, this.drag.y, this.maxVelocity.y) - this.velocity.y) / 2;
    this.velocity.y += vd; delta = this.velocity.y * FlxG.elapsed; this.velocity.y += vd; this.y += delta;
  }
  getScreenXY(pt, cam) {
    if (cam == null) cam = FlxG.camera;
    pt.x = nudge(this.x - Math.trunc(cam.scroll.x * this.scrollFactor.x));
    pt.y = nudge(this.y - Math.trunc(cam.scroll.y * this.scrollFactor.y));
    return pt;
  }
  onScreen(cam) {
    if (cam == null) cam = FlxG.camera;
    this.getScreenXY(this._point, cam);
    return this._point.x + this.width > 0 && this._point.x < cam.width && this._point.y + this.height > 0 && this._point.y < cam.height;
  }
  getMidpoint(pt) {
    if (pt == null) pt = new FlxPoint();
    pt.x = this.x + this.width * 0.5; pt.y = this.y + this.height * 0.5;
    return pt;
  }
  reset(x, y) {
    this.revive();
    this.touching = NONE; this.wasTouching = NONE;
    this.x = x; this.y = y; this.last.x = x; this.last.y = y;
    this.velocity.x = 0; this.velocity.y = 0;
  }
}

class FlxAnim {
  constructor(name, frames, fps = 0, looped = true) {
    this.name = name; this.frames = frames; this.looped = looped;
    this.delay = fps > 0 ? 1 / fps : 0;
  }
}

class FlxSprite extends FlxObject {
  constructor(x = 0, y = 0, img = null) {
    super(x, y);
    this.offset = new FlxPoint(); this.origin = new FlxPoint(); this.scale = new FlxPoint(1, 1);
    this._alpha = 1; this._color = 0xffffff; this.blend = null; this.antialiasing = false;
    this.finished = false; this._facing = RIGHT;
    this._animations = []; this._flipped = 0; this._curAnim = null; this._curFrame = 0; this._curIndex = 0; this._frameTimer = 0;
    this._pixels = null; this.frameWidth = 0; this.frameHeight = 0; this.frames = 0;
    this.dirty = false; this._callback = null;
    this._srcX = 0; this._srcY = 0; this._flipX = false;
    // For sheets baked with loadRotatedGraphic-style rotation frames: when
    // FlxG.rotateAtDraw is on, draw frame 0 rotated by frame * 360/frames
    // (clockwise, about the frame centre) instead of blitting the baked frame.
    this.rotationFrames = 0;
    if (img == null) this.makeGraphic(16, 16, 0xffff0000); // FlxSprite default 16x16 graphic
    else this.loadGraphic(img);
  }
  static image(img) { return typeof img === 'string' ? FlxG.images[img] : img; }
  loadGraphic(img, animated = false, reverse = false, width = 0, height = 0, unique = false) {
    img = FlxSprite.image(img);
    if (img.prescaled) {
      // A canvas already rendered at FlxG.scale (e.g. the procedural HD sky):
      // logical size is its size / scale, and it is blitted 1:1.
      const S = FlxG.scale;
      this._pixels = { img: img, width: img.width / S, height: img.height / S, solid: null, prescaled: true };
      this._flipped = 0;
      this.width = this.frameWidth = img.width / S;
      this.height = this.frameHeight = img.height / S;
      this.resetHelpers();
      return this;
    }
    this._pixels = { img: img, width: img.width, height: img.height, solid: null, prescaled: false };
    this._flipped = reverse ? img.width : 0;
    if (width === 0) width = animated ? img.height : img.width;
    this.width = this.frameWidth = width;
    if (height === 0) height = animated ? width : img.height;
    this.height = this.frameHeight = height;
    this.resetHelpers();
    return this;
  }
  makeGraphic(width, height, color = 0xffffffff) {
    this._pixels = { img: null, width: width, height: height, solid: color, prescaled: false };
    this._flipped = 0;
    this.width = this.frameWidth = width;
    this.height = this.frameHeight = height;
    this.resetHelpers();
    return this;
  }
  resetHelpers() {
    this.origin.make(this.frameWidth * 0.5, this.frameHeight * 0.5);
    this.frames = (this._pixels.width / this.frameWidth) * (this._pixels.height / this.frameHeight);
    this._curIndex = 0;
    this.calcFrame();
  }
  get alpha() { return this._alpha; }
  set alpha(a) { if (a > 1) a = 1; if (a < 0) a = 0; this._alpha = a; }
  get color() { return this._color; }
  set color(c) { this._color = c & 0x00ffffff; this.dirty = true; }
  get facing() { return this._facing; }
  set facing(f) { if (this._facing !== f) this.dirty = true; this._facing = f; }
  get frame() { return this._curIndex; }
  set frame(f) { this._curAnim = null; this._curIndex = f; this.dirty = true; }
  addAnimation(name, frames, fps = 0, looped = true) { this._animations.push(new FlxAnim(name, frames, fps, looped)); }
  play(name, force = false) {
    if (!force && this._curAnim != null && name === this._curAnim.name && (this._curAnim.looped || !this.finished)) return;
    this._curFrame = 0; this._curIndex = 0; this._frameTimer = 0;
    for (const a of this._animations) {
      if (a.name === name) {
        this._curAnim = a;
        this.finished = a.delay <= 0;
        this._curIndex = a.frames[this._curFrame];
        this.dirty = true;
        return;
      }
    }
    FlxG.log('WARNING: No animation called "' + name + '"');
  }
  postUpdate() { super.postUpdate(); this.updateAnimation(); }
  updateAnimation() {
    if (this._curAnim != null && this._curAnim.delay > 0 && (this._curAnim.looped || !this.finished)) {
      this._frameTimer += FlxG.elapsed;
      while (this._frameTimer > this._curAnim.delay) {
        this._frameTimer -= this._curAnim.delay;
        if (this._curFrame === this._curAnim.frames.length - 1) {
          if (this._curAnim.looped) this._curFrame = 0;
          this.finished = true;
        } else {
          ++this._curFrame;
        }
        this._curIndex = this._curAnim.frames[this._curFrame];
        this.dirty = true;
      }
    }
    if (this.dirty) this.calcFrame();
  }
  calcFrame() {
    let ix = this._curIndex * this.frameWidth, iy = 0;
    const w = this._flipped ? this._flipped : this._pixels.width;
    if (ix >= w) { iy = Math.floor(ix / w) * this.frameHeight; ix %= w; }
    this._flipX = !!this._flipped && this._facing === LEFT;
    this._srcX = ix; this._srcY = iy;
    if (this._callback != null) this._callback(this._curAnim != null ? this._curAnim.name : null, this._curFrame, this._curIndex);
    this.dirty = false;
  }
  draw() {
    if (this.dirty) this.calcFrame();
    // Positions are snapped to whole game pixels exactly as Flixel does
    // (truncated camera scroll, truncated position, 1e-7 nudge) in both
    // classic and HD, so the two modes place every sprite identically and
    // all pixel-art blocks share one grid; HD just draws each game pixel as
    // an S x S block.
    const S = FlxG.scale;
    for (const cam of FlxG.cameras) {
      if (!this.onScreen(cam)) continue;
      const px = nudge(this.x - Math.trunc(cam.scroll.x * this.scrollFactor.x) - this.offset.x);
      const py = nudge(this.y - Math.trunc(cam.scroll.y * this.scrollFactor.y) - this.offset.y);
      this.drawFrame(cam.ctx, Math.trunc(px) * S, Math.trunc(py) * S);
    }
  }
  drawFrame(ctx, dx, dy) {
    const p = this._pixels, S = FlxG.scale, fw = this.frameWidth * S, fh = this.frameHeight * S;
    const sw = this.frameWidth, sh = this.frameHeight;
    ctx.globalAlpha = this._alpha;
    ctx.globalCompositeOperation = this.blend || 'source-over';
    ctx.imageSmoothingEnabled = false; // HD mode is nearest-neighbour everywhere
    if (p.prescaled) {
      ctx.drawImage(p.img, 0, 0, p.img.width, p.img.height, dx, dy, p.img.width, p.img.height);
    } else if (this.rotationFrames > 0 && FlxG.rotateAtDraw && S > 1) {
      // The baked sheets are frame 0 rotated clockwise by index * 360/N
      // (verified against the SWF's sheets), so this reproduces them exactly
      // up to resampling, at full resolution.
      const angle = (this._curIndex % this.rotationFrames) * (2 * Math.PI / this.rotationFrames);
      ctx.save();
      ctx.translate(dx + fw / 2, dy + fh / 2);
      ctx.rotate(angle);
      ctx.drawImage(p.img, 0, 0, sw, sh, -fw / 2, -fh / 2, fw, fh);
      ctx.restore();
    } else if (p.solid != null) {
      let c = p.solid;
      const a = ((c >>> 24) & 255) / 255 * this._alpha;
      let r = (c >>> 16) & 255, g = (c >>> 8) & 255, b = c & 255;
      if (this._color !== 0xffffff) {
        r = Math.round(r * (((this._color >>> 16) & 255) * 0.00392));
        g = Math.round(g * (((this._color >>> 8) & 255) * 0.00392));
        b = Math.round(b * ((this._color & 255) * 0.00392));
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
      ctx.fillRect(dx, dy, fw, fh);
    } else if (this._flipX) {
      ctx.save();
      ctx.translate(dx + fw, dy);
      ctx.scale(-1, 1);
      ctx.drawImage(p.img, this._srcX, this._srcY, sw, sh, 0, 0, fw, fh);
      ctx.restore();
    } else {
      ctx.drawImage(p.img, this._srcX, this._srcY, sw, sh, dx, dy, fw, fh);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
}

// ---------------------------------------------------------------------------
// FlxText - renders through an offscreen canvas exactly once per change, then
// blits like any other sprite (as Flixel does with its TextField -> BitmapData).
// ---------------------------------------------------------------------------
class FlxText extends FlxSprite {
  constructor(x, y, width, text = null, embedded = true) {
    super(x, y, null);
    this._text = text == null ? '' : text;
    this._font = 'system'; this._size = 8; this._tcolor = 0xffffff; this._align = 'left'; this._shadow = 0;
    this.width = width;
    this._canvas = document.createElement('canvas');
    this._pixels = { img: this._canvas, width: width, height: 1, solid: null, prescaled: true };
    this._regen = true;
    FlxG.texts.add(this);
    this.calcText();
  }
  destroy() { FlxG.texts.delete(this); super.destroy(); }
  get text() { return this._text; }
  set text(t) { t = String(t); if (t === this._text) return; this._text = t; this._regen = true; this.calcText(); }
  get color() { return this._tcolor; }
  set color(c) { this._tcolor = c & 0xffffff; this._regen = true; this.calcText(); }
  setFormat(font = null, size = 8, color = 0xffffff, align = null, shadow = 0) {
    this._font = font || ''; this._size = size; this._tcolor = color & 0xffffff; this._align = align || 'left'; this._shadow = shadow;
    this._regen = true; this.calcText();
    return this;
  }
  static metrics(font, size) {
    // EnglishTowne: unitsPerEm 1024, ascent 897, descent 246, lineGap 170.
    if (font === 'EnglishTowne') return { ascent: Math.round(897 / 1024 * size), descent: Math.round(246 / 1024 * size), leading: Math.round(170 / 1024 * size) };
    return { ascent: Math.round(size * 0.9), descent: Math.round(size * 0.25), leading: 0 };
  }
  _cssFont() { return (this._size * FlxG.scale) + 'px ' + (this._font === 'EnglishTowne' ? '"EnglishTowne"' : 'monospace'); }
  _wrap(ctx) {
    const inner = (this.width - 4) * FlxG.scale, lines = [];
    for (const para of this._text.split(/\r?\n/)) {
      const words = para.split(' ');
      let line = '';
      for (const w of words) {
        const test = line.length ? line + ' ' + w : w;
        if (ctx.measureText(test).width > inner && line.length) { lines.push(line); line = w; }
        else line = test;
      }
      lines.push(line);
    }
    return lines;
  }
  calcText() {
    const S = FlxG.scale;
    const m = FlxText.metrics(this._font, this._size);          // logical (1x) metrics: layout/height
    const ms = FlxText.metrics(this._font, this._size * S);     // metrics at render scale
    const lineH = m.ascent + m.descent + m.leading;
    const lineHS = ms.ascent + ms.descent + ms.leading;
    const ctx = this._canvas.getContext('2d');
    ctx.font = this._cssFont();
    const lines = this._text.length ? this._wrap(ctx) : [];
    const n = Math.max(1, lines.length);
    const h = n * lineH + 4;
    this.height = this.frameHeight = h; this.frameWidth = this.width;
    this._canvas.width = this.width * S; this._canvas.height = n * lineHS + 4 * S;
    this._pixels.width = this.width; this._pixels.height = h;
    if (!lines.length) return;
    ctx.font = this._cssFont();
    ctx.textBaseline = 'alphabetic';
    const inner = (this.width - 4) * S, W = this.width * S, gutter = 2 * S;
    const draw = (color, ox, oy) => {
      ctx.fillStyle = rgbToCss(color);
      for (let i = 0; i < lines.length; i++) {
        const lw = ctx.measureText(lines[i]).width;
        let x = gutter;
        if (this._align === 'center') {
          // Flixel special-cases single-line centred text (left-aligned + integer offset).
          x = lines.length === 1 ? gutter + Math.floor((W - lw) / 2) : gutter + (inner - lw) / 2;
        } else if (this._align === 'right') x = gutter + inner - lw;
        ctx.fillText(lines[i], x + ox, gutter + ms.ascent + i * lineHS + oy);
      }
    };
    if (this._shadow > 0) draw(this._shadow, S, S);
    draw(this._tcolor, 0, 0);
  }
  calcFrame() { this._srcX = 0; this._srcY = 0; this._flipX = false; this.dirty = false; }
}

// ---------------------------------------------------------------------------
// FlxGroup / FlxState
// ---------------------------------------------------------------------------
class FlxGroup extends FlxBasic {
  constructor(maxSize = 0) {
    super();
    this.members = []; this.length = 0; this._maxSize = maxSize; this._marker = 0;
  }
  add(obj) {
    if (this.members.indexOf(obj) >= 0) return obj;
    for (let i = 0; i < this.members.length; i++) {
      if (this.members[i] == null) {
        this.members[i] = obj;
        if (i >= this.length) this.length = i + 1;
        return obj;
      }
    }
    if (this._maxSize > 0 && this.members.length >= this._maxSize) return obj;
    this.members.push(obj);
    this.length = this.members.length;
    return obj;
  }
  recycle(Cls) {
    if (this._maxSize > 0) {
      if (this.length < this._maxSize) return this.add(new Cls());
      const b = this.members[this._marker++];
      if (this._marker >= this._maxSize) this._marker = 0;
      return b;
    }
    for (let i = 0; i < this.length; i++) { const b = this.members[i]; if (b != null && !b.exists) return b; }
    if (Cls == null) return null;
    return this.add(new Cls());
  }
  remove(obj, splice = false) {
    const i = this.members.indexOf(obj);
    if (i < 0) return null;
    if (splice) { this.members.splice(i, 1); --this.length; } else this.members[i] = null;
    return obj;
  }
  update() {
    for (let i = 0; i < this.length; i++) {
      const b = this.members[i];
      if (b != null && b.exists && b.active) { b.preUpdate(); b.update(); b.postUpdate(); }
    }
  }
  draw() {
    for (let i = 0; i < this.length; i++) {
      const b = this.members[i];
      if (b != null && b.exists && b.visible) b.draw();
    }
  }
  kill() {
    for (let i = 0; i < this.length; i++) { const b = this.members[i]; if (b != null && b.exists) b.kill(); }
    super.kill();
  }
  destroy() {
    for (let i = 0; i < this.members.length; i++) { const b = this.members[i]; if (b != null) b.destroy(); }
    this.members.length = 0; this.length = 0;
  }
}
class FlxState extends FlxGroup { create() {} }

// ---------------------------------------------------------------------------
// FlxEmitter / FlxParticle
// ---------------------------------------------------------------------------
class FlxParticle extends FlxSprite {
  constructor() { super(); this.lifespan = 0; this.friction = 500; }
  update() {
    if (this.lifespan <= 0) return;
    this.lifespan -= FlxG.elapsed;
    if (this.lifespan <= 0) this.kill();
    if (this.touching) { if (this.angularVelocity !== 0) this.angularVelocity = -this.angularVelocity; }
    if (this.acceleration.y > 0) {
      if (this.touching & FLOOR) {
        this.drag.x = this.friction;
        if (!(this.wasTouching & FLOOR)) {
          if (this.velocity.y < -this.elasticity * 10) { if (this.angularVelocity !== 0) this.angularVelocity *= -this.elasticity; }
          else { this.velocity.y = 0; this.angularVelocity = 0; }
        }
      } else this.drag.x = 0;
    }
  }
  onEmit() {}
}

class FlxEmitter extends FlxGroup {
  constructor(x = 0, y = 0, size = 0) {
    super(size);
    this.x = x; this.y = y; this.width = 0; this.height = 0;
    this.minParticleSpeed = new FlxPoint(-100, -100); this.maxParticleSpeed = new FlxPoint(100, 100);
    this.minRotation = -360; this.maxRotation = 360;
    this.gravity = 0; this.particleDrag = new FlxPoint(); this.frequency = 0.1; this.lifespan = 3; this.bounce = 0;
    this._quantity = 0; this._counter = 0; this._explode = true; this.on = false; this._timer = 0;
  }
  setXSpeed(min = 0, max = 0) { this.minParticleSpeed.x = min; this.maxParticleSpeed.x = max; }
  setYSpeed(min = 0, max = 0) { this.minParticleSpeed.y = min; this.maxParticleSpeed.y = max; }
  setRotation(min = 0, max = 0) { this.minRotation = min; this.maxRotation = max; }
  update() {
    if (this.on) {
      if (this._explode) {
        this.on = false;
        let n = this._quantity; if (n <= 0 || n > this.members.length) n = this.members.length;
        for (let i = 0; i < n; i++) this.emitParticle();
        this._quantity = 0;
      } else {
        this._timer += FlxG.elapsed;
        while (this.frequency > 0 && this._timer > this.frequency && this.on) {
          this._timer -= this.frequency;
          this.emitParticle();
          if (this._quantity > 0 && ++this._counter >= this._quantity) { this.on = false; this._quantity = 0; }
        }
      }
    }
    super.update();
  }
  emitParticle() {
    const p = this.recycle(FlxParticle);
    p.lifespan = this.lifespan;
    p.elasticity = this.bounce;
    p.reset(this.x - (p.width >> 1) + FlxG.random() * this.width, this.y - (p.height >> 1) + FlxG.random() * this.height);
    p.visible = true;
    p.velocity.x = this.minParticleSpeed.x !== this.maxParticleSpeed.x ? this.minParticleSpeed.x + FlxG.random() * (this.maxParticleSpeed.x - this.minParticleSpeed.x) : this.minParticleSpeed.x;
    p.velocity.y = this.minParticleSpeed.y !== this.maxParticleSpeed.y ? this.minParticleSpeed.y + FlxG.random() * (this.maxParticleSpeed.y - this.minParticleSpeed.y) : this.minParticleSpeed.y;
    p.acceleration.y = this.gravity;
    p.angularVelocity = this.minRotation !== this.maxRotation ? this.minRotation + FlxG.random() * (this.maxRotation - this.minRotation) : this.minRotation;
    if (p.angularVelocity !== 0) p.angle = FlxG.random() * 360 - 180;
    p.drag.x = this.particleDrag.x; p.drag.y = this.particleDrag.y;
    p.onEmit();
  }
}

// ---------------------------------------------------------------------------
// FlxSound (WebAudio). Semantics follow org.flixel.FlxSound:
//   * play() on a sound that is already playing does nothing
//   * transform volume = (mute ? 0 : 1) * FlxG.volume * volume * fadeAdjust
// ---------------------------------------------------------------------------
class FlxSound extends FlxBasic {
  constructor() {
    super();
    this._buffer = null; this._looped = false; this._src = null; this._gain = null;
    this._volume = 1; this._volumeAdjust = 1; this._position = 0; this._startedAt = 0;
    this._fadeOutTimer = 0; this._fadeOutTotal = 0; this._pauseOnFadeOut = false; this._fadeInTimer = 0; this._fadeInTotal = 0;
    this.survive = false;
    // FlxSound.createSound(): a sound that isn't playing is neither `exists`
    // nor `active`, so FlxG.pauseSounds() leaves it alone (pausing an idle
    // sound would set _position = -1 and make play() a no-op for good).
    this.exists = false; this.active = false; this.visible = false;
    FlxG.liveSounds.add(this);
  }
  loadEmbedded(name, looped = false) {
    this.stop();
    this._position = 0; this._volume = 1; this._volumeAdjust = 1;
    this._fadeOutTimer = 0; this._fadeOutTotal = 0; this._pauseOnFadeOut = false; this._fadeInTimer = 0; this._fadeInTotal = 0;
    this.active = false; this.visible = false;
    this._buffer = FlxG.soundBuffers[name] || null;
    this._looped = looped;
    this.updateTransform();
    this.exists = true;
    return this;
  }
  get volume() { return this._volume; }
  set volume(v) { if (v < 0) v = 0; else if (v > 1) v = 1; this._volume = v; this.updateTransform(); }
  get playing() { return this._src != null; }
  play() {
    if (this._position < 0) return;
    if (this._src != null) { this._position = 0; return; }
    const ac = FlxG.audio;
    if (ac == null || this._buffer == null) { this._position = 0; return; }
    const src = ac.createBufferSource();
    src.buffer = this._buffer;
    src.loop = this._looped;
    const gain = ac.createGain();
    src.connect(gain); gain.connect(ac.destination);
    this._src = src; this._gain = gain;
    this.updateTransform();
    let offset = this._position > 0 ? this._position / 1000 : 0;
    if (this._looped) offset = offset % this._buffer.duration;
    this._startedAt = ac.currentTime - offset;
    src.onended = () => { if (this._src === src) this.stopped(); };
    src.start(0, offset);
    this.active = true;
    this._position = 0;
  }
  pause() {
    if (this._src == null) { this._position = -1; return; }
    const ac = FlxG.audio;
    this._position = Math.max(0, (ac.currentTime - this._startedAt) * 1000);
    if (this._looped) { const len = this._buffer.duration * 1000; while (this._position >= len) this._position -= len; }
    const src = this._src; this._src = null;
    src.onended = null; try { src.stop(); } catch (e) {}
    this.active = false;
  }
  stop() {
    this._position = 0;
    if (this._src != null) { const src = this._src; src.onended = null; try { src.stop(); } catch (e) {} this.stopped(); }
  }
  stopped() { this._src = null; this._gain = null; this.exists = false; this.active = false; }
  fadeOut(seconds, pauseInstead = false) {
    this._pauseOnFadeOut = pauseInstead; this._fadeInTimer = 0;
    this._fadeOutTimer = seconds; this._fadeOutTotal = seconds;
  }
  update() {
    if (this._position !== 0) return;
    let fade = 1;
    if (this._fadeOutTimer > 0) {
      this._fadeOutTimer -= FlxG.elapsed;
      if (this._fadeOutTimer <= 0) { if (this._pauseOnFadeOut) this.pause(); else this.stop(); }
      fade = this._fadeOutTimer / this._fadeOutTotal; if (fade < 0) fade = 0;
    } else if (this._fadeInTimer > 0) {
      this._fadeInTimer -= FlxG.elapsed;
      fade = this._fadeInTimer / this._fadeInTotal; if (fade < 0) fade = 0;
      fade = 1 - fade;
    }
    this._volumeAdjust = fade;
    this.updateTransform();
  }
  updateTransform() {
    if (this._gain != null) this._gain.gain.value = (FlxG.mute ? 0 : 1) * FlxG.volume * this._volume * this._volumeAdjust;
  }
  kill() { super.kill(); if (this._src != null) this.stop(); }
  destroy() { this.kill(); this._buffer = null; FlxG.liveSounds.delete(this); }
}

// ---------------------------------------------------------------------------
// FlxSave (localStorage)
// ---------------------------------------------------------------------------
class FlxSave {
  constructor() { this.data = null; this.name = null; }
  bind(name) {
    this.name = 'FlxSave:' + name;
    try { const raw = localStorage.getItem(this.name); this.data = raw ? JSON.parse(raw) : {}; }
    catch (e) { this.data = {}; }
    return true;
  }
  flush() { try { localStorage.setItem(this.name, JSON.stringify(this.data)); } catch (e) {} return true; }
  erase() { this.data = {}; try { localStorage.removeItem(this.name); } catch (e) {} }
}

// ---------------------------------------------------------------------------
// TweenNano (com.greensock.TweenNano, wall-clock based, quad easeOut default)
// ---------------------------------------------------------------------------
class TweenNano {
  constructor(target, duration, vars) {
    this.target = target; this.duration = duration; this.vars = vars;
    // AS3: `duration == 0 && vars.delay == 0 && ...` where an absent delay
    // (undefined == 0) is false.
    this.active = duration === 0 && vars.delay === 0 && vars.immediateRender !== false;
    this._ease = typeof vars.ease === 'function' ? vars.ease : TweenNano.easeOut;
    this._propTweens = []; this._initted = false; this.gc = false; this.ratio = 0;
    this.useFrames = false;
    const delay = 'delay' in vars ? Number(vars.delay) : 0;
    this.startTime = TweenNano._time + delay;
    const list = TweenNano._masterList.get(target);
    if (list == null || vars.overwrite == null || Number(vars.overwrite) === 1) TweenNano._masterList.set(target, [this]);
    else list.push(this);
    if (vars.immediateRender === true || this.active) this.renderTime(0);
  }
  static to(target, duration, vars) { return new TweenNano(target, duration, vars); }
  static delayedCall(delay, fn, params = null) {
    return new TweenNano(fn, 0, { delay: delay, onComplete: fn, onCompleteParams: params, overwrite: 0 });
  }
  static easeOut(t, b, c, d) { return -1 * (t = t / d) * (t - 2); }
  static updateAll(nowSeconds) {
    TweenNano._time = nowSeconds;
    for (const [target, list] of Array.from(TweenNano._masterList.entries())) {
      for (let i = list.length - 1; i > -1; i--) {
        const tw = list[i];
        if (tw.active || (!tw.gc && TweenNano._time >= tw.startTime)) tw.renderTime(TweenNano._time - tw.startTime);
        else if (tw.gc) list.splice(i, 1);
      }
      if (list.length === 0) TweenNano._masterList.delete(target);
    }
  }
  static killTweensOf(target) { TweenNano._masterList.delete(target); }
  static killAll() { TweenNano._masterList.clear(); }
  init() {
    for (const p in this.vars) {
      if (p in TweenNano._reserved) continue;
      const v = this.vars[p], cur = this.target[p];
      this._propTweens.push([p, cur, typeof v === 'number' ? v - cur : Number(v)]);
    }
    this._initted = true;
  }
  renderTime(t) {
    if (!this._initted) this.init();
    if (t >= this.duration) { t = this.duration; this.ratio = 1; }
    else if (t <= 0) this.ratio = 0;
    else this.ratio = this._ease(t, 0, 1, this.duration);
    for (let i = this._propTweens.length - 1; i > -1; i--) {
      const pt = this._propTweens[i];
      this.target[pt[0]] = pt[1] + this.ratio * pt[2];
    }
    if (this.vars.onUpdate) this.vars.onUpdate.apply(null, this.vars.onUpdateParams || []);
    if (t === this.duration) this.complete(true);
  }
  complete(skipRender = false) {
    if (!skipRender) { this.renderTime(this.duration); return; }
    this.kill();
    if (this.vars.onComplete) this.vars.onComplete.apply(null, this.vars.onCompleteParams || []);
  }
  kill() { this.gc = true; this.active = false; }
}
TweenNano._time = 0;
TweenNano._masterList = new Map();
TweenNano._reserved = { ease: 1, delay: 1, useFrames: 1, overwrite: 1, onComplete: 1, onCompleteParams: 1, runBackwards: 1, immediateRender: 1, onUpdate: 1, onUpdateParams: 1 };

// ---------------------------------------------------------------------------
// FlxGame - the fixed-step game loop (org.flixel.FlxGame)
// ---------------------------------------------------------------------------
class FlxGame {
  constructor(width, height, StateClass, zoom = 1, framerate = 60, flashFramerate = 30, options = {}) {
    FlxG._game = this;
    FlxG.width = width; FlxG.height = height;
    this.zoom = zoom;
    this.presentScale = options.presentScale || zoom; // classic-mode integer multiple of the 320x240 buffer
    this._step = Math.floor(1000 / framerate);              // uint(1000/60) = 16
    this._maxAccumulation = Math.floor(2000 / flashFramerate - 1); // uint(2000/30 - 1) = 65
    this._accumulator = this._step;
    this._total = 0;
    this._lostFocus = false;
    this._iState = StateClass;
    this._state = null; this._requestedState = null; this._requestedReset = true;
    // Flash ran the whole loop on ENTER_FRAME at stage.frameRate (30 fps for
    // CLOP): two 16 ms ticks per frame, key state sampled once per frame, one
    // draw. frameRate = 0 processes every animation frame instead.
    this.frameInterval = options.frameRate ? 1000 / options.frameRate : 0;
    this.autoFrameRate = !!options.autoFrameRate; // 30 fps loop in classic, per-frame in HD
    this._nextFrame = -1e9;
    // Flash Player delivers keyboard events once per frame, just before
    // ENTER_FRAME. Queue them and flush every 1000/inputRate ms so that key
    // edges quantise to 33 ms frames even when the loop itself runs per
    // animation frame. inputRate = 0 applies events immediately.
    this.inputInterval = options.inputRate ? 1000 / options.inputRate : 0;
    this._nextInputFrame = -1e9;
    this._inputQueue = [];
    this._pending = 0;
    this._tweensDue = false; this._tweenTime = 0;
    this.frameCount = 0; this.drawCount = 0; this.stepCount = 0;
    this.useSoundHotKeys = true;
    this.screen = options.canvas;
    this.sctx = this.screen.getContext('2d', { alpha: false });
    this.width = width; this.height = height;
    this.onScaleChange();
    FlxG.keys = new Keyboard();
    FlxG.resetCameras();
    this.loadSoundSettings();
    this._bindEvents();
  }
  // Presentation is always an exact integer multiple of the buffer, never a
  // stretch. Classic: the 320x240 buffer at the largest whole multiple that
  // fits the window (presentScale, set by the page). HD: the width*scale x
  // height*scale buffer presented 1:1.
  onScaleChange() {
    const S = FlxG.scale;
    const k = S === 1 ? this.presentScale : 1;
    this.screen.width = this.width * S * k;
    this.screen.height = this.height * S * k;
    this.sctx.imageSmoothingEnabled = false;
    if (this.autoFrameRate) this.frameInterval = S > 1 ? 0 : 1000 / 30;
    if (this._state && this._state.onScaleChange) this._state.onScaleChange();
  }
  _bindEvents() {
    // FlxGame.onKeyDown forwards every key (the sound hot keys included) to
    // FlxG.keys; FlxGame.onKeyUp handles the hot keys and swallows their
    // key-up. Keys with a browser modifier are forwarded but never
    // preventDefault'ed, so shortcuts keep working.
    window.addEventListener('keydown', (e) => {
      if (!e.repeat) this.queueInput('down', e.code);
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^(Key[A-Z]|Space|Arrow)/.test(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => {
      if (this.useSoundHotKeys && !(e.metaKey || e.ctrlKey || e.altKey)) {
        switch (e.code) {
          case 'Digit0': case 'Numpad0': FlxG.mute = !FlxG.mute; this.soundChanged(); return;
          case 'Minus': case 'NumpadSubtract': FlxG.mute = false; FlxG.volume = Math.max(0, FlxG.volume - 0.1); this.soundChanged(); return;
          case 'Equal': case 'NumpadAdd': FlxG.mute = false; FlxG.volume = Math.min(1, FlxG.volume + 0.1); this.soundChanged(); return;
        }
      }
      this.queueInput('up', e.code);
    });
    window.addEventListener('blur', () => this.onFocusLost());
    window.addEventListener('focus', () => this.onFocus());
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.onFocusLost(); else this.onFocus(); });
  }
  // Flixel's sound tray persists volume/mute in FlxSave("flixel").data.sound
  // and restores them at startup.
  loadSoundSettings() {
    const save = new FlxSave();
    if (!save.bind('flixel') || !save.data.sound) return;
    const s = save.data.sound;
    if (typeof s.volume === 'number') FlxG.volume = Math.max(0, Math.min(1, s.volume));
    if (typeof s.mute === 'boolean') FlxG.mute = s.mute;
  }
  soundChanged() {
    FlxG.updateVolumes();
    const save = new FlxSave();
    if (save.bind('flixel')) { save.data.sound = { mute: FlxG.mute, volume: FlxG.volume }; save.flush(); }
  }
  queueInput(type, code) {
    if (this.inputInterval > 0) this._inputQueue.push(type, code);
    else this.applyInput(type, code);
  }
  applyInput(type, code) {
    if (type === 'activate') {
      if (this._state && this._state.onActivate) this._state.onActivate();
    } else if (type === 'down') {
      FlxG.keys.handleKeyDown(code);
      if (this._state && this._state.onKeyDown) this._state.onKeyDown(code);
    } else {
      FlxG.keys.handleKeyUp(code);
    }
  }
  // Fixed-rate clocks (the 30 fps frame clock and the 30 Hz input-frame
  // clock) are phase-locked: each boundary is scheduled one interval after
  // the previous one, not one interval after the animation frame that
  // happened to service it, so the average rate is exact on any display
  // (re-phasing to the frame gave 25 Hz on 75 Hz displays, 28.8 Hz on
  // 144 Hz). A boundary that is more than a whole interval late (tab was
  // hidden) re-phases to now instead of running several boundaries at once.
  _clockDue(key, now, interval) {
    if (interval <= 0) return true;
    const tol = FlxGame.CLOCK_TOLERANCE;
    if (now < this[key] - tol) return false;
    let next = this[key] + interval;
    if (next <= now + tol) next = now + interval;
    this[key] = next;
    return true;
  }
  flushInput() {
    const q = this._inputQueue;
    for (let i = 0; i < q.length; i += 2) this.applyInput(q[i], q[i + 1]);
    q.length = 0;
  }
  onFocus() {
    if (!this._lostFocus) return;
    FlxG.resetInput();
    this._inputQueue.length = 0;
    this._lostFocus = false;
    FlxG.playSounds();
  }
  onFocusLost() {
    if (this._lostFocus) return;
    this._lostFocus = true;
    FlxG.pauseSounds();
    this.drawFocusScreen();
  }
  // FlxGame.createFocusScreen: a 50% black veil with a 65% white play
  // triangle (side = min(w, h)/2/3 of the 2x stage), shown while unfocused.
  drawFocusScreen() {
    const ctx = this.sctx, w = this.screen.width, h = this.screen.height;
    const cx = w / 2, cy = h / 2, s = Math.floor(Math.min(cx, cy) / 3);
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(255,255,255,0.65)';
    ctx.beginPath(); ctx.moveTo(cx - s, cy - s); ctx.lineTo(cx + s, cy); ctx.lineTo(cx - s, cy + s); ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  start() {
    this._total = Math.floor(performance.now());
    TweenNano._time = Math.floor(performance.now()) * 0.001; // getTimer() is integer ms
    const frame = (now) => {
      this.onEnterFrame(now);
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }
  onEnterFrame(now) {
    if (!this._clockDue('_nextFrame', now, this.frameInterval)) return;
    this.frameCount++;
    // An "input frame" is Flash's frame: keyboard events are delivered, then
    // the ticks owed for the elapsed interval are computed. When the loop runs
    // per animation frame (frameInterval == 0) those ticks are spread over
    // the following animation frames, one per frame, and drawn after each, so
    // the tick/key-state sequence is identical to Flash's while the screen
    // updates at the display rate.
    const spread = this.frameInterval === 0 && this.inputInterval > 0;
    if (!this._lostFocus) {
      const inputDue = this._clockDue('_nextInputFrame', now, this.inputInterval);
      const boundary = !spread || inputDue;
      if (boundary) {
        // Ticks still owed from the previous input frame, and that frame's
        // tween update, come first (Flash: ticks, then TweenNano's ENTER_FRAME).
        while (this._pending > 0) { this._pending--; this.stepCount++; this.step(); }
        if (this._tweensDue) { this._tweensDue = false; TweenNano.updateAll(this._tweenTime); }
        if (inputDue) this.flushInput();
        const mark = Math.floor(now);
        const elapsedMS = mark - this._total;
        this._total = mark;
        this._accumulator += elapsedMS;
        if (this._accumulator > this._maxAccumulation) this._accumulator = this._maxAccumulation;
        while (this._accumulator > this._step) {
          this._accumulator -= this._step;
          this._pending++;
        }
        this._tweenTime = Math.floor(now) * 0.001; // getTimer() is integer ms
        this._tweensDue = true;
        if (spread) { if (this._pending > 0) { this._pending--; this.stepCount++; this.step(); } }
        else { while (this._pending > 0) { this._pending--; this.stepCount++; this.step(); } }
      } else if (this._pending > 0) {
        this._pending--; this.stepCount++; this.step();
      }
      // TweenNano ran on Flash's ENTER_FRAME, after the frame's ticks, with
      // the frame's wall-clock time.
      if (this._tweensDue && this._pending === 0) { this._tweensDue = false; TweenNano.updateAll(this._tweenTime); }
      this.draw();
    } else {
      this._total = Math.floor(now);
    }
  }
  switchState() {
    FlxG.resetCameras();
    FlxG.resetInput();
    if (this._state != null) this._state.destroy();
    this._state = this._requestedState;
    this._state.create();
  }
  step() {
    if (this._requestedReset) {
      this._requestedReset = false;
      this._requestedState = new this._iState();
      FlxG.resetInput();
      FlxG.timeScale = 1; FlxG.elapsed = 0;
    }
    if (this._state !== this._requestedState) this.switchState();
    FlxG.updateInput();
    this.update();
  }
  update() {
    FlxG.elapsed = FlxG.timeScale * (this._step / 1000);
    this._state.update();
    FlxG.updateCameras();
  }
  draw() {
    if (this._state == null) return; // no logic tick has run yet this frame
    this.drawCount++;
    FlxG.lockCameras();
    this._state.draw();
    FlxG.unlockCameras();
    const cam = FlxG.camera, S = FlxG.scale, k = S === 1 ? this.presentScale : 1;
    this.sctx.imageSmoothingEnabled = false;
    if (k === 1) this.sctx.drawImage(cam.buffer, cam.x * S, cam.y * S);
    else this.sctx.drawImage(cam.buffer, 0, 0, cam.buffer.width, cam.buffer.height, cam.x * k, cam.y * k, cam.buffer.width * k, cam.buffer.height * k);
  }
}

// How early (ms) an animation frame may service a fixed-rate clock boundary;
// absorbs requestAnimationFrame timestamp jitter without letting a boundary
// slip a whole frame late.
FlxGame.CLOCK_TOLERANCE = 4;

// ---------------------------------------------------------------------------
// Asset loading
// ---------------------------------------------------------------------------
const FlxAssets = {
  // build.js inlines CLOP_ASSETS (logical path -> content-addressed path);
  // when running from source it is undefined and paths are used as-is.
  resolve(url) { return (typeof CLOP_ASSETS !== 'undefined' && CLOP_ASSETS[url]) || url; },
  loadImage(name, url, attempt = 0) {
    url = FlxAssets.resolve(url);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { FlxG.images[name] = img; resolve(img); };
      img.onerror = () => {
        if (attempt < 2) setTimeout(() => FlxAssets.loadImage(name, url, attempt + 1).then(resolve, reject), 250);
        else reject(new Error('Failed to load image ' + url));
      };
      img.src = url;
    });
  },
  // `info` = { rate, samples, seek } from the SWF's DefineSound tag. Flash skips
  // `seek` decoded samples (the MP3 encoder delay) and plays exactly `samples`
  // samples; browsers can't do that themselves because these MP3s carry no
  // gapless (LAME/Info) metadata, so trim the decoded buffer the same way.
  async loadSound(name, url, info = null, attempt = 0) {
    url = FlxAssets.resolve(url);
    let res;
    try { res = await fetch(url); if (!res.ok) throw new Error('HTTP ' + res.status); }
    catch (e) { if (attempt < 2) { await new Promise((r) => setTimeout(r, 250)); return FlxAssets.loadSound(name, url, info, attempt + 1); } throw e; }
    const data = await res.arrayBuffer();
    if (FlxG.audio == null) return null;
    let buf = await FlxG.audio.decodeAudioData(data.slice(0));
    if (info) buf = FlxAssets.trimSound(buf, info);
    FlxG.soundBuffers[name] = buf;
    return buf;
  },
  trimSound(buf, info) {
    const scale = buf.sampleRate / info.rate;
    const start = Math.round(info.seek * scale);
    let length = Math.round(info.samples * scale);
    if (start + length > buf.length) length = Math.max(0, buf.length - start);
    if (length <= 0) return buf;
    const out = FlxG.audio.createBuffer(buf.numberOfChannels, length, buf.sampleRate);
    for (let c = 0; c < buf.numberOfChannels; c++) {
      out.copyToChannel(buf.getChannelData(c).subarray(start, start + length), c);
    }
    return out;
  },
};
